import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  FormControl,
  useColorMode,
  ModalFooter,
} from '@chakra-ui/react';
import {
  customerUsersApi,
  SetCustomTypeRequest,
} from '@texas/api/endpoints/customerUsersApi';
import CreatableSelect from 'react-select/creatable';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { reactSelectStyle, reactSelectTheme } from '@texas/resources/styles';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';

interface Props extends SharedDisclosureProps {
  customerUserId: number;
  customType: string;
  onUpdate: () => void;
}

export function SetCustomTypeModal({
  isOpen,
  onClose,
  onUpdate,
  customType,
  customerUserId,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('general.update')}</ModalHeader>
        <ModalCloseButton />
        <ModalInner
          onUpdate={onUpdate}
          customType={customType}
          customerUserId={customerUserId}
        />
      </ModalContent>
    </Modal>
  );
}

function ModalInner({
  onUpdate,
  customType,
  customerUserId,
}: {
  onUpdate: () => void;
  customType: string;
  customerUserId: number;
}) {
  const { data, refetch, loading } = useApiResource(
    customerUsersApi.getAllCustomTypes,
  );
  const { t } = useTranslation();
  const { colorMode } = useColorMode();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = useForm<SetCustomTypeRequest>({
    defaultValues: {
      customType,
    },
  });

  const { request: updateReq, error } = useApiRequest(
    customerUsersApi.setCustomType,
  );

  const performRequest = (data: SetCustomTypeRequest) =>
    request(updateReq, [customerUserId, data], () => onUpdate());

  return (
    <form onSubmit={handleSubmit(performRequest)}>
      <ModalBody>
        {error && <ErrorDetails error={error} />}
        <FormControl>
          <TexasFormLabel>{t('customerUser.custom')}</TexasFormLabel>
          <Controller
            control={control}
            name="customType"
            render={({ field }) => (
              <CreatableSelect
                styles={reactSelectStyle<false>({ mode: colorMode })}
                theme={(theme) => reactSelectTheme(theme, colorMode)}
                isLoading={loading}
                isClearable={true}
                value={{
                  value: field.value,
                  label: field.value,
                }}
                onChange={(e) => {
                  field.onChange(e?.label ?? null);
                }}
                options={data?.map((x) => ({
                  label: x,
                  value: x,
                }))}
              />
            )}
          />
          <ErrorLabel text={errors.customType?.message} />
        </FormControl>
      </ModalBody>
      <ModalFooter>
        <SubmitButton disabled={!isValid} loading={isSubmitting}>
          {t('general.update')}
        </SubmitButton>
      </ModalFooter>
    </form>
  );
}
